import { Box, Paper, useMediaQuery } from "@material-ui/core";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import LineAndScatterChart from "src/components/ScoringChart/LineAndScatterChart";
import { beautifulTooltip } from "src/components/ScoringChart/tool-utils/BeautifulTooltip";
import DateGroupButton from "src/components/ScoringChart/tool-utils/DateGroupButton";
import TitleTypography, { NoteTypography } from "src/components/Typography/TitleTypography";
import DetailMonitoringWrapper from "src/components/Wrapper/DetailMonitoringWrapper";
import { MONITORING } from "src/config/constants";
import { useUserContext } from "src/context/config/userContext";
import {
  useUserMonitoringChartSelector,
  useUserMonitoringContext,
  useUserMonitoringSelector,
} from "src/context/monitoring/userMonitoringContext";
import { numberWithCommas, toFixed } from "src/services/utils";
import ButtonAddressTextField from "../ButtonAddressTextField";
import LiquidationHistoryTable from "./LiquidationHistoryTable";

const getOptions = (setTimestamp) => ({
  chart: {
    height: 400,
    events: {
      click: function (event) {
        const timestamp = toFixed(event.xAxis[0].value / 1000);
        setTimestamp(timestamp);
      },
    },
  },
  xAxis: {
    type: "datetime",
    labels: {
      enabled: true,
    },
    gridLineWidth: 1,
  },
  yAxis: [
    {
      title: {
        text: "Health Factor",
      },
      gridLineWidth: 0,
    },
    {
      opposite: true,
      title: {
        text: "Liquidation History",
      },
      gridLineWidth: 0,
    },
  ],
  ...beautifulTooltip((value) => numberWithCommas(toFixed(value, 4))),
  plotOptions: {
    series: {
      events: {
        click: function (event) {
          const timestamp = toFixed(event.point.category / 1000);
          setTimestamp(timestamp);
        },
      },
    },
  },
});

const dateOption = () => {
  var currentTime = Math.floor(Date.now() / 1000);
  return [
    { label: "1D", from: currentTime - 86400, to: currentTime, id: "1D" },
    { label: "1W", from: currentTime - 604800, to: currentTime, id: "1W" },
    { label: "1M", from: currentTime - 2592000, to: currentTime, id: "1M" },
    { label: "3M", from: currentTime - 7776000, to: currentTime, id: "3M" },
  ];
};

export default function LiquidationHistory() {
  const { pool } = useParams();
  const match = useMediaQuery((theme) => theme.breakpoints.only("xs"));
  const [timestamp, setTimestamp] = useState(null);

  const { fetchLiquidationHistory } = useUserMonitoringSelector((state) => state.fetch);
  const { selectedAddress, setSelectedAddress } = useUserMonitoringContext();
  const liquidationHistory = useUserMonitoringChartSelector(MONITORING.USER.liquidationHistory, pool);
  const { role } = useUserContext();

  useEffect(() => {
    if (liquidationHistory?.active === undefined) {
      var currentTime = Math.floor(Date.now() / 1000);
      fetchLiquidationHistory(pool, { from: currentTime - 604800, to: currentTime }, { active: 1 });
    } else fetchLiquidationHistory(pool, liquidationHistory?.metadata, { active: liquidationHistory?.active });
  }, [liquidationHistory?.active, pool, fetchLiquidationHistory]);

  const { series, check } = useMemo(() => {
    const check = liquidationHistory?.healthFactor?.length > 0;
    let series = "";
    if (check) {
      series = [
        { type: "line", name: "Health Factor", data: [].concat(liquidationHistory.healthFactor) },
        {
          type: "scatter",
          name: "Liquidation History",
          data: [].concat(liquidationHistory.liquidationChart),
          yAxis: 1,
        },
      ];
    } else
      series = [
        { type: "line", name: "Health Factor", data: [] },
        { type: "scatter", name: "Liquidation History", data: [], yAxis: 1 },
      ];
    return { series: series, check: check };
  }, [liquidationHistory]);

  const eventData = useMemo(() => {
    if (timestamp) return liquidationHistory?.liquidation?.[timestamp];
    return null;
  }, [timestamp]);

  return (
    <DetailMonitoringWrapper
      isAuthorization={true}
      navigation={{
        text: "user monitoring",
        name: "health factor and liquidation history",
        link: `/user-monitoring/${pool}`,
      }}
      selectLink="/user-monitoring/health-factor-and-liquidation-history"
      Component={{
        description: (
          <>
            <NoteTypography>
              The chart shows the health factor and liquidation history of a user. This data indicates the current
              status of the user.
            </NoteTypography>
            <NoteTypography>
              For example, the health factor keeps decreasing implies bad status, and the wallet could be soon
              liquidated if its health factor is lower than 1.
            </NoteTypography>
            <NoteTypography>
              Each liquidation is represented by a spot. Clicking the spot, we can see the detailed liquidation
              transaction.
            </NoteTypography>
          </>
        ),
      }}
    >
      {role === "admin" && (
        <ButtonAddressTextField
          defaultValue={selectedAddress}
          events={{ onConfirm: (value) => setSelectedAddress(value) }}
        />
      )}
      <Paper style={{ marginTop: "1rem" }}>
        <Box mb={2} display="flex" justifyContent="flex-end" alignItems="center">
          <DateGroupButton
            fullWidth={match}
            defaultActive={liquidationHistory?.active ?? 0}
            picker={{
              continueClick: (from, to, active) => {
                fetchLiquidationHistory(pool, { from, to }, { active });
              },
            }}
            config={dateOption().map((item, _) => ({
              data: item,
              onClick: (data, active) => {
                fetchLiquidationHistory(pool, { from: data.from, to: data.to }, { active });
              },
            }))}
          />
        </Box>
        {check ? (
          <LineAndScatterChart series={series} option={getOptions(setTimestamp)} />
        ) : role === "admin" ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <TitleTypography textProps={{ variant: "h4" }} title="This account doesn't have any transactions" />
          </Box>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <TitleTypography textProps={{ variant: "h4" }} title="You don't have any transactions" />
          </Box>
        )}
      </Paper>
      <Box mt={2}>
        <LiquidationHistoryTable timestamp={timestamp} data={eventData} />
      </Box>
    </DetailMonitoringWrapper>
  );
}
